import {
  listarEncaminhamentos,
  normalizeEncaminhamentos
} from "./workflow-status-utils";

/**
 * Utilitários puros para prever a ordem do fluxo de um Business Plan a partir
 * dos encaminhamentos parametrizados nas etapas do workflow.
 *
 * Modelo assumido:
 * - O BP sempre termina em uma "Aprovação final" ou "Reprovação final"
 *   (encaminhamentos com `final`).
 * - Cada etapa (workflow) possui status; o status que avança o fluxo tem um
 *   encaminhamento apontando para a PRÓXIMA etapa (`workflow_id` de outra
 *   etapa). Encaminhamentos "internos" (`workflow_id` = própria etapa) e os
 *   terminais (`final`) não geram arestas entre etapas.
 * - A etapa INICIAL é aquela que não é destino de nenhum encaminhamento de
 *   outra etapa (nenhuma etapa/status anterior a ela).
 *
 * Regra usada para ordenar a grade de Configurações > Workflow:
 * a partir da etapa da Aprovação final, caminhar de trás para frente pelos
 * encaminhamentos até encontrar a etapa inicial; o caminho (início → final)
 * é exibido primeiro e as demais etapas (ramos paralelos, Reprovação final,
 * etc.) mantêm a ordem original do backend após o caminho.
 */

/**
 * Monta o grafo dirigido de encaminhamentos ENTRE etapas.
 * Self-links (`workflow_id` da própria etapa) são ignorados.
 * @param {Array<{id: number, status?: Array}>} etapas
 * @returns {{ destinos: Map<number, number[]>, origens: Map<number, number[]> }}
 *   `destinos`/`origens`: por id da etapa, ids das etapas destino/origem.
 */
export function grafoEncaminhamentos(etapas) {
  const lista = Array.isArray(etapas) ? etapas.filter(e => e && e.id != null) : [];

  const destinos = new Map();
  const origens = new Map();

  lista.forEach(etapa => {
    destinos.set(etapa.id, []);
    origens.set(etapa.id, []);
  });

  lista.forEach(etapa => {
    if (!Array.isArray(etapa.status)) {
      return;
    }

    etapa.status.forEach(status => {
      const encs = normalizeEncaminhamentos(status && status.encaminhamentos);

      encs.forEach(enc => {
        const ehEntreEtapas =
          enc.tipo_destino === "etapa" &&
          enc.workflow_id != null &&
          enc.workflow_id !== etapa.id &&
          destinos.has(enc.workflow_id);

        if (!ehEntreEtapas) {
          return;
        }

        if (!destinos.get(etapa.id).includes(enc.workflow_id)) {
          destinos.get(etapa.id).push(enc.workflow_id);
        }

        if (!origens.get(enc.workflow_id).includes(etapa.id)) {
          origens.get(enc.workflow_id).push(etapa.id);
        }
      });
    });
  });

  return { destinos, origens };
}

/**
 * Retorna a primeira etapa que possui um encaminhamento terminal do tipo
 * informado (`final_aprovado` | `final_reprovado`).
 * @param {Array} etapas
 * @param {"final_aprovado"|"final_reprovado"} tipoFinal
 * @returns {Object|undefined}
 */
export function etapaComFinal(etapas, tipoFinal) {
  const lista = Array.isArray(etapas) ? etapas : [];

  return lista.find(etapa =>
    (Array.isArray(etapa.status) ? etapa.status : []).some(status =>
      normalizeEncaminhamentos(status && status.encaminhamentos).some(
        enc => enc.tipo_destino === tipoFinal
      )
    )
  );
}

/**
 * Ids das etapas sem nenhuma etapa/status anterior (não são destino de
 * encaminhamento de outra etapa) — candidatas a início do fluxo.
 * @param {Array} etapas
 * @param {Map<number, number[]>} [origens] grafo já montado (opcional)
 * @returns {number[]}
 */
export function idsEtapasInicio(etapas, origens) {
  const lista = Array.isArray(etapas) ? etapas.filter(e => e && e.id != null) : [];

  if (!origens) {
    origens = grafoEncaminhamentos(lista).origens;
  }

  return lista
    .filter(etapa => (origens.get(etapa.id) || []).length === 0)
    .map(etapa => etapa.id);
}

/**
 * Status que TRAVARIAM o BP: status sem nenhum encaminhamento configurado em
 * etapa que participa do fluxo (recebe ou envia encaminhamento entre etapas).
 *
 * Etapas "estado final" isoladas (sem aresta de entrada nem saída — ex.: as
 * etapas terminais Aprovado/Reprovado usadas como estado do BP, cujo status
 * fica com `encaminhamentos: null`) NÃO geram aviso, para evitar falso
 * positivo no fluxo padrão.
 * @param {Array} etapas
 * @returns {Array<{etapaId: number, etapaNome: string, statusId: number|null, statusNome: string}>}
 */
export function statusesSemEncaminhamento(etapas) {
  const lista = Array.isArray(etapas) ? etapas.filter(e => e && e.id != null) : [];

  if (!lista.length) {
    return [];
  }

  const { destinos, origens } = grafoEncaminhamentos(lista);
  const achados = [];

  lista.forEach(etapa => {
    const ehAtiva =
      (origens.get(etapa.id) || []).length > 0 ||
      (destinos.get(etapa.id) || []).length > 0;

    if (!ehAtiva || !Array.isArray(etapa.status)) {
      return;
    }

    etapa.status.forEach(status => {
      if (!status) {
        return;
      }

      const temEncaminhamento =
        normalizeEncaminhamentos(status.encaminhamentos).length > 0;

      if (temEncaminhamento) {
        return;
      }

      const statusId =
        status.id != null
          ? status.id
          : status.status_id != null
            ? status.status_id
            : null;

      achados.push({
        etapaId: etapa.id,
        etapaNome: etapa.nome || `Etapa #${etapa.id}`,
        statusId,
        statusNome:
          status.tipo && status.tipo.nome
            ? status.tipo.nome
            : `Status #${statusId != null ? statusId : "?"}`
      });
    });
  });

  return achados;
}

/**
 * Chave de um status no grafo: `etapaId::statusId`.
 *
 * A identidade usa o `status_id` **tipo** (`WorkflowStatusTipo`) porque é o que
 * o backend grava em `encaminhamentos.status_id`
 * (`Rule::exists(WorkflowStatusTipo::class, 'id')` — ver
 * `WorkflowStatusController`, branch `development`).
 *
 * @param {number|string} etapaId
 * @param {number|string} statusId
 * @returns {string}
 */
export function chaveStatus(etapaId, statusId) {
  return `${etapaId}::${statusId}`;
}

/**
 * Monta o grafo dirigido de STATUS do workflow (nós = status; arestas =
 * encaminhamentos entre status, inclusive o interno/Ajuste da própria etapa).
 *
 * @param {Array<{id: number, nome?: string, status?: Array}>} etapas
 * @returns {{
 *   arestas: Map<string, Set<string>>,
 *   temFinal: Set<string>,
 *   metas: Map<string, {etapaId: number, etapaNome: string, statusId: number, statusNome: string}>
 * }}
 */
export function grafoStatus(etapas) {
  const lista = Array.isArray(etapas) ? etapas.filter(e => e && e.id != null) : [];
  const arestas = new Map();
  const temFinal = new Set();
  const metas = new Map();

  lista.forEach(etapa => {
    (Array.isArray(etapa.status) ? etapa.status : []).forEach(status => {
      if (!status || status.status_id == null) {
        return;
      }

      const chave = chaveStatus(etapa.id, status.status_id);

      metas.set(chave, {
        etapaId: etapa.id,
        etapaNome: etapa.nome || `Etapa #${etapa.id}`,
        statusId: status.status_id,
        statusNome:
          status.tipo && status.tipo.nome
            ? status.tipo.nome
            : `Status #${status.status_id}`
      });

      if (!arestas.has(chave)) {
        arestas.set(chave, new Set());
      }
    });
  });

  lista.forEach(etapa => {
    (Array.isArray(etapa.status) ? etapa.status : []).forEach(status => {
      if (!status || status.status_id == null) {
        return;
      }

      const origem = chaveStatus(etapa.id, status.status_id);

      listarEncaminhamentos(status).forEach(enc => {
        if (!enc) {
          return;
        }

        if (enc.final) {
          temFinal.add(origem);
        }

        if (enc.workflow_id != null && enc.status_id != null) {
          arestas.get(origem).add(chaveStatus(enc.workflow_id, enc.status_id));
        }
      });
    });
  });

  return { arestas, temFinal, metas };
}

/**
 * Status que NÃO alcançam nenhum encaminhamento final (Aprovação/Reprovação
 * final) seguindo os encaminhamentos — a partir deles o BP não termina.
 *
 * @param {Array} etapas
 * @returns {string[]} Chaves (`etapaId::statusId`) sem alcance de final
 */
export function statusesSemAlcanceDeFinal(etapas) {
  const { arestas, temFinal } = grafoStatus(etapas);

  if (!arestas.size) {
    return [];
  }

  const reverso = new Map();

  arestas.forEach((destinos, origem) => {
    destinos.forEach(destino => {
      if (!reverso.has(destino)) {
        reverso.set(destino, new Set());
      }

      reverso.get(destino).add(origem);
    });
  });

  const alcancaFinal = new Set(temFinal);
  const fila = [...temFinal];

  while (fila.length) {
    const atual = fila.shift();

    (reverso.get(atual) || []).forEach(anterior => {
      if (!alcancaFinal.has(anterior)) {
        alcancaFinal.add(anterior);
        fila.push(anterior);
      }
    });
  }

  return [...arestas.keys()].filter(chave => !alcancaFinal.has(chave));
}

/**
 * Componentes fortemente conectados (Tarjan iterativo).
 *
 * @param {string[]} nos
 * @param {Map<string, Set<string>>} arestas
 * @returns {string[][]} Um array de componentes (cada um com suas chaves)
 */
function componentesFortementeConectados(nos, arestas) {
  const indices = new Map();
  const low = new Map();
  const pilha = [];
  const naPilha = new Set();
  const componentes = [];
  let indice = 0;

  nos.forEach(raiz => {
    if (indices.has(raiz)) {
      return;
    }

    const trabalho = [
      { no: raiz, filhos: [...(arestas.get(raiz) || [])], i: 0 }
    ];

    indices.set(raiz, indice);
    low.set(raiz, indice);
    indice += 1;
    pilha.push(raiz);
    naPilha.add(raiz);

    while (trabalho.length) {
      const atual = trabalho[trabalho.length - 1];

      if (atual.i < atual.filhos.length) {
        const filho = atual.filhos[atual.i];
        atual.i += 1;

        if (!indices.has(filho)) {
          indices.set(filho, indice);
          low.set(filho, indice);
          indice += 1;
          pilha.push(filho);
          naPilha.add(filho);
          trabalho.push({
            no: filho,
            filhos: [...(arestas.get(filho) || [])],
            i: 0
          });
        } else if (naPilha.has(filho)) {
          low.set(atual.no, Math.min(low.get(atual.no), indices.get(filho)));
        }

        continue;
      }

      trabalho.pop();

      if (trabalho.length) {
        const pai = trabalho[trabalho.length - 1];
        low.set(pai.no, Math.min(low.get(pai.no), low.get(atual.no)));
      }

      if (low.get(atual.no) === indices.get(atual.no)) {
        const componente = [];
        let no;

        do {
          no = pilha.pop();
          naPilha.delete(no);
          componente.push(no);
        } while (no !== atual.no);

        componentes.push(componente);
      }
    }
  });

  return componentes;
}

/**
 * Ciclos **sem saída**: ciclos entre status em que **nenhum** membro consegue
 * alcançar um encaminhamento final (Aprovação ou Reprovação final).
 *
 * Loops **com saída são válidos e não são reportados** — ex.: um status levar
 * ao Ajuste da mesma etapa e o Ajuste voltar ao status anterior, desde que
 * exista outro encaminhamento levando a outra etapa/status (ou ao final). O
 * ciclo só é destacado quando o BP não tem como sair dele para encerrar o
 * fluxo — inclusive em ciclos de vários saltos (A → B → C → A).
 *
 * @param {Array} etapas
 * @returns {Array<{statuses: Array<{etapaId, etapaNome, statusId, statusNome}>, total: number}>}
 *   Um item por ciclo sem saída
 */
export function ciclosSemSaida(etapas) {
  const { arestas, metas } = grafoStatus(etapas);

  if (!arestas.size) {
    return [];
  }

  const presos = new Set(statusesSemAlcanceDeFinal(etapas));

  return componentesFortementeConectados([...arestas.keys()], arestas)
    .filter(componente => componente.every(chave => presos.has(chave)))
    .filter(componente => {
      if (componente.length > 1) {
        return true;
      }

      const destinos = arestas.get(componente[0]);

      return !!destinos && destinos.has(componente[0]);
    })
    .map(componente => ({
      statuses: componente.map(chave => metas.get(chave)).filter(Boolean),
      total: componente.length
    }));
}

/**
 * Preve o caminho principal do BP (início → Aprovação final).
 *
 * Caminha de trás para frente: parte da etapa que encaminha para a
 * "Aprovação final" e segue pelos encaminhamentos (BFS sobre as origens) até
 * encontrar uma etapa sem nenhuma etapa/status anterior. O resultado é o
 * menor caminho (em número de saltos entre etapas).
 *
 * Avisos possíveis em `avisos`:
 * - `sem_aprovacao_final`: **nenhum** status tem encaminhamento para a
 *   Aprovação final (final "aprovado") — o BP não conclui o fluxo;
 * - `loop_sem_saida`: há ciclo de encaminhamentos **sem saída** (nenhum status
 *   do ciclo alcança Aprovação/Reprovação final) — detalhes em `ciclosSemSaida`;
 * - `caminho_incompleto`: o caminho encontrado não alcança a Aprovação final;
 * - `status_sem_encaminhamento`: etapa ativa possui status travado
 *   (sem encaminhamento) — detalhes em `statusesSemEncaminhamento`.
 *
 * Loops **com saída são válidos** e não geram aviso (ver `ciclosSemSaida`).
 * Exibição em tela (avisos + modal "Caminho previsto") em
 * `configuracoes/windows/workflow/index.vue`; o detalhamento dos ciclos foi
 * adicionado em 10/09/2026.
 *
 * @param {Array} etapas
 * @returns {{
 *   inicioId: number|null,
 *   finalId: number|null,
 *   caminhoIds: number[],
 *   avisos: string[],
 *   ciclosSemSaida: Array<{statuses: Array<Object>, total: number}>
 * }}
 */
export function preverCaminhoPrincipal(etapas) {
  const lista = Array.isArray(etapas) ? etapas.filter(e => e && e.id != null) : [];
  const ciclos = ciclosSemSaida(lista);

  if (!lista.length) {
    return {
      inicioId: null,
      finalId: null,
      caminhoIds: [],
      avisos: [],
      ciclosSemSaida: ciclos
    };
  }

  const { origens } = grafoEncaminhamentos(lista);
  const avisos = [];

  // Ciclo sem saída (loop válido não entra aqui).
  if (ciclos.length) {
    avisos.push("loop_sem_saida");
  }

  // Status travado (sem encaminhamento) em etapa que participa do fluxo.
  if (statusesSemEncaminhamento(lista).length) {
    avisos.push("status_sem_encaminhamento");
  }

  const etapaFinal = etapaComFinal(lista, "final_aprovado");
  const finalId = etapaFinal ? etapaFinal.id : null;

  if (!finalId) {
    avisos.push("sem_aprovacao_final");
    return {
      inicioId: null,
      finalId: null,
      caminhoIds: [],
      avisos,
      ciclosSemSaida: ciclos
    };
  }

  // BFS reverso: do final em direção ao início, sem revisitar nós.
  const fila = [finalId];
  const visitados = new Set([finalId]);
  const pai = new Map();
  let inicioId = null;

  while (fila.length && inicioId == null) {
    const atual = fila.shift();
    const anteriores = origens.get(atual) || [];

    if (anteriores.length === 0) {
      inicioId = atual;
      break;
    }

    anteriores.forEach(anterior => {
      if (visitados.has(anterior)) {
        return;
      }

      visitados.add(anterior);
      pai.set(anterior, atual);
      fila.push(anterior);
    });
  }

  if (inicioId == null) {
    // Todas as etapas alcançáveis a partir do final têm etapa anterior (loop de
    // etapas). Isso, por si só, não é um problema: o destaque de ciclo sem
    // saída é dado por `ciclosSemSaida` quando nenhum status do ciclo alcança
    // Aprovação/Reprovação final.
    return {
      inicioId: null,
      finalId,
      caminhoIds: [],
      avisos,
      ciclosSemSaida: ciclos
    };
  }

  const caminhoIds = [inicioId];
  let atual = inicioId;

  while (atual !== finalId && pai.has(atual)) {
    atual = pai.get(atual);
    caminhoIds.push(atual);
  }

  if (caminhoIds[caminhoIds.length - 1] !== finalId) {
    avisos.push("caminho_incompleto");
  }

  return { inicioId, finalId, caminhoIds, avisos, ciclosSemSaida: ciclos };
}

/**
 * Reordena a lista de etapas para refletir, na medida do possível, o caminho
 * que o BP percorrerá: o caminho principal (início → Aprovação final) vem
 * primeiro e as demais etapas mantêm a ordem original do backend.
 * @param {Array} etapas
 * @returns {{ etapasOrdenadas: Array, previsao: object }}
 */
export function reordenarEtapasParaFluxo(etapas) {
  const lista = Array.isArray(etapas) ? etapas : [];

  if (!lista.length) {
    return { etapasOrdenadas: [], previsao: preverCaminhoPrincipal(lista) };
  }

  const previsao = preverCaminhoPrincipal(lista);
  const idsDoCaminho = previsao.caminhoIds || [];

  if (!idsDoCaminho.length) {
    // Sem caminho (ex.: sem Aprovação final, loop sem início definido) mantemos
    // a ordem do backend; os avisos ao usuário saem de `previsao.avisos`
    // (`loop_sem_saida`, `sem_aprovacao_final`, `status_sem_encaminhamento`...).
    return { etapasOrdenadas: [...lista], previsao };
  }

  const etapasPorId = new Map(lista.map(etapa => [etapa.id, etapa]));
  const principais = idsDoCaminho
    .map(id => etapasPorId.get(id))
    .filter(Boolean);
  const idsDoCaminhoSet = new Set(idsDoCaminho);
  const demais = lista.filter(etapa => !idsDoCaminhoSet.has(etapa.id));

  return { etapasOrdenadas: [...principais, ...demais], previsao };
}


